import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { BackButton } from "@/components/BackButton";
import { StarField } from "@/components/alignment/StarField";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { GAMES, gameBySlug } from "@/data/games";
import { SITE_URL } from "@/lib/siteConfig";
import { toast } from "sonner";
import { Play, ArrowRight, ArrowLeft, Share2, Sparkles, Check, Flame, Trophy } from "lucide-react";

export default function GamePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [playing, setPlaying] = useState(false);
  const game = gameBySlug(slug);

  if (!game) {
    return (
      <div className="min-h-screen bg-[#0A0A14] text-slate-200" data-testid="game-not-found">
        <Seo title="Game not found | InnerArcade" description="This game could not be found. Explore our free browser games." path={`/games/${slug}`} noindex />
        <Header />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <h1 className="font-head font-900 text-3xl text-slate-50 mb-4">Game not found</h1>
          <Link to="/mindful" data-testid="game-notfound-back" className="text-[#A78BFA] hover:text-[#2DD4BF] transition-colors">Browse all games →</Link>
        </div>
      </div>
    );
  }

  const Icon = game.icon;
  const Comp = game.Component;
  const accent = game.color;
  const related = game.related.map(gameBySlug).filter(Boolean);
  const canonical = `/games/${game.slug}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "VideoGame",
    name: game.name,
    description: game.seoDescription,
    url: `${SITE_URL}${canonical}`,
    genre: game.genre,
    gamePlatform: "Web browser",
    applicationCategory: "GameApplication",
    operatingSystem: "Any (web browser)",
    playMode: "SinglePlayer",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD", availability: "https://schema.org/InStock" },
    publisher: { "@type": "Organization", name: "InnerArcade", url: SITE_URL },
  };

  const play = () => {
    if (user) {
      setPlaying(true);
      setTimeout(() => document.getElementById("play-surface")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    } else {
      navigate("/auth");
    }
  };

  const share = async () => {
    const url = `${SITE_URL}${canonical}`;
    const shareData = { title: `${game.name} — InnerArcade`, text: game.short, url };
    try {
      if (navigator.share) { await navigator.share(shareData); return; }
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — share it with a friend!");
    } catch (e) { /* user cancelled */ }
  };

  return (
    <div className="min-h-screen relative text-slate-200 overflow-hidden" data-testid="game-page">
      <Seo title={game.seoTitle} description={game.seoDescription} path={canonical} keywords={game.keywords} type="article" jsonLd={jsonLd} />
      <div className="absolute inset-0" style={{ background: `radial-gradient(900px 500px at 12% -8%, ${accent}22, transparent 60%), radial-gradient(800px 600px at 95% 5%, rgba(88,28,135,0.35), transparent 60%), linear-gradient(180deg,#070512,#0A0A14)` }} />
      <StarField color={accent} />
      <div className="relative z-10">
        <Header />
        <article className="max-w-4xl mx-auto px-6 py-10">
          <BackButton className="mb-6" fallback="/mindful" />

          {/* Hero */}
          <div className="rounded-3xl border p-7 sm:p-9 bg-white/[0.03] backdrop-blur-md" style={{ borderColor: `${accent}33`, boxShadow: `0 0 70px ${accent}1f` }}>
            <div className="flex items-center gap-2 mb-4">
              <Icon className="w-7 h-7" style={{ color: accent }} />
              <span className="text-xs uppercase tracking-[0.25em]" style={{ color: accent }}>{game.genre} · Free to play</span>
            </div>
            <h1 className="font-head font-900 text-4xl sm:text-5xl text-slate-50 leading-[0.95] mb-3">{game.name}</h1>
            <p className="text-lg text-slate-300 mb-6 max-w-2xl">{game.intro}</p>
            <div className="flex flex-wrap items-center gap-3">
              <button data-testid="game-play-now" onClick={play}
                className="inline-flex items-center gap-2 rounded-xl px-7 py-3.5 text-base font-semibold text-slate-900 hover:-translate-y-0.5 transition-transform duration-300"
                style={{ background: accent }}>
                <Play className="w-5 h-5" /> {user ? "Play Now" : "Sign in to Play"}
              </button>
              <button data-testid="game-share" onClick={share}
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-5 py-3.5 text-sm text-slate-200 hover:border-white/40 transition-colors duration-300">
                <Share2 className="w-4 h-4" /> Share
              </button>
            </div>
          </div>

          {/* Play surface (in-page) */}
          {playing && user && (
            <div id="play-surface" className="mt-6 rounded-3xl border bg-white/[0.03] backdrop-blur-md p-6 sm:p-8" style={{ borderColor: `${accent}33`, boxShadow: `0 0 70px ${accent}22` }} data-testid="game-play-surface">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-head font-900 text-2xl text-slate-50">{game.name}</h2>
                <button data-testid="game-play-close" onClick={() => setPlaying(false)} className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100 transition-colors">
                  <ArrowLeft className="w-4 h-4" /> Overview
                </button>
              </div>
              <Comp />
            </div>
          )}

          {/* How to play */}
          <section className="mt-10" data-testid="game-how-to-play">
            <h2 className="font-head font-700 text-2xl text-slate-50 mb-4">How to play {game.name}</h2>
            <ol className="space-y-3">
              {game.howToPlay.map((step, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-300">
                  <span className="shrink-0 w-6 h-6 rounded-full grid place-items-center text-xs font-bold text-slate-900" style={{ background: accent }}>{i + 1}</span>
                  <span className="text-base leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
          </section>

          {/* Scoring + Daily challenge */}
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            <section className="rounded-2xl border border-white/10 p-6 bg-white/[0.02]" data-testid="game-scoring">
              <div className="flex items-center gap-2 mb-3"><Trophy className="w-5 h-5" style={{ color: accent }} /><h3 className="font-head font-700 text-lg text-slate-50">Scoring & rewards</h3></div>
              <p className="text-sm text-slate-400 leading-relaxed">{game.scoring}</p>
            </section>
            <section className="rounded-2xl border border-white/10 p-6 bg-white/[0.02]" data-testid="game-daily-challenge">
              <div className="flex items-center gap-2 mb-3"><Flame className="w-5 h-5" style={{ color: accent }} /><h3 className="font-head font-700 text-lg text-slate-50">Keep coming back</h3></div>
              <p className="text-sm text-slate-400 leading-relaxed">{game.repeat}</p>
            </section>
          </div>

          {/* Related games */}
          <section className="mt-12" data-testid="game-related">
            <p className="text-sm tracking-[0.25em] uppercase text-slate-400 mb-4">Related games</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {related.map((r) => {
                const RIcon = r.icon;
                return (
                  <Link key={r.slug} to={`/games/${r.slug}`} data-testid={`game-related-${r.slug}`}
                    className="group flex items-start gap-4 rounded-2xl border p-5 bg-white/[0.03] hover:-translate-y-1 transition-transform duration-300"
                    style={{ borderColor: `${r.color}44` }}>
                    <RIcon className="w-8 h-8 shrink-0" style={{ color: r.color }} />
                    <div>
                      <h3 className="font-head font-700 text-base text-slate-50">{r.name}</h3>
                      <p className="text-sm text-slate-400 mt-1">{r.short}</p>
                      <span className="mt-2 inline-flex items-center gap-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ color: r.color }}>Play <ArrowRight className="w-3 h-3" /></span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>

          {/* Footer CTA */}
          <div className="mt-12 rounded-2xl border border-violet-500/25 p-7 bg-white/[0.03] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="game-footer-cta">
            <div>
              <div className="flex items-center gap-2 mb-1"><Sparkles className="w-5 h-5 text-[#A78BFA]" /><h3 className="font-head font-700 text-lg text-slate-50">Play with purpose</h3></div>
              <p className="text-sm text-slate-400 flex items-center gap-2"><Check className="w-4 h-4 text-[#2DD4BF]" /> Free forever · earns Journey XP · no download</p>
            </div>
            <button data-testid="game-footer-play" onClick={play} className="inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold text-slate-900" style={{ background: accent }}>
              <Play className="w-4 h-4" /> {user ? "Play Now" : "Sign in to Play"}
            </button>
          </div>
        </article>
      </div>
    </div>
  );
}

export { GAMES };
